/**
 * ServiceUpgradeRequired.js
 *
 * Reusable "Feature Locked" screen shown when a user accesses
 * a service that is not enabled for their account.
 *
 * Props:
 *  - serviceName:   string — Display name (e.g. "DoqSolv", "Workflows")
 *  - serviceId:     string — Internal ID (e.g. "doqsolv", "workflows")
 *  - description:   string — Optional description of what the service does
 *  - onViewServices: () => void — Optional callback to navigate to API Services tab
 */

import React from 'react';

const ServiceUpgradeRequired = ({
  serviceName = 'This Service',
  serviceId,
  description,
  onViewServices,
}) => {
  return (
    <div className="fade-in flex flex-col items-center justify-center py-20 px-6 text-center">
      {/* Lock Icon */}
      <div className="w-24 h-24 mb-6 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center shadow-inner">
        <svg
          className="w-12 h-12 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
      </div>

      {/* Heading */}
      <h2 className="text-2xl font-bold text-gray-900 mb-2">
        {serviceName} — Service Required
      </h2>

      {/* Description */}
      <p className="text-sm text-gray-500 max-w-md mb-2 leading-relaxed">
        This advanced feature is currently not enabled for your account.
      </p>

      {description && (
        <p className="text-xs text-gray-400 max-w-md mb-6 leading-relaxed">
          {description}
        </p>
      )}

      {!description && <div className="mb-6" />}

      {/* Feature highlights */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 max-w-md mb-8">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 text-left">
          What you'll get with {serviceName}:
        </h3>
        <ul className="text-left text-sm text-gray-600 space-y-2">
          <li className="flex items-start gap-2">
            <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Full API access with consumption tracking
          </li>
          <li className="flex items-start gap-2">
            <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Dashboard panel with complete management UI
          </li>
          <li className="flex items-start gap-2">
            <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Postman collection with pre-configured API key
          </li>
        </ul>
      </div>

      {/* CTAs */}
      <div className="flex flex-col sm:flex-row gap-3">
        <a
          href="mailto:support@matricastamps.com?subject=Enable%20Service%20Request"
          className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-white rounded-lg shadow-sm transition"
          style={{ background: 'linear-gradient(135deg, #6366f1, #4f46e5)' }}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          Contact Administrator to Enable
        </a>

        {onViewServices && (
          <button
            onClick={onViewServices}
            className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
            View All API Services
          </button>
        )}
      </div>
    </div>
  );
};

export default ServiceUpgradeRequired;
